import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import * as Ui from "./styles";
import GenericCollapsibleList from "../GenericCollapsibleList";
import { MatchIcon } from "src/components/Icons";
import CrossQuestionsAlternatives from "../../widget/CrossQuestionsAlternatives";
import { isNullOrEmptyArray } from "src/util/arrays";
import { useEventBus } from "src/hooks";
import {
  ON_COMBINE_QUESTIONS,
  ON_OPEN_SEND_COMBINATION_MODAL,
  ON_RENDER_EVOLUTIVE_CROSS_FOOTER_OPTIONS,
} from "src/hooks/EventBus/eventTypes";
import { mapQuestionsToSurveyIdList } from "../../parsers/combinedQuestions";
import { handleOnExecuteCombinationActions } from "../../actionHandler";
import useFlashMessage from "src/hooks/FlashMessage";
import { COMBINED_QUESTIONS, COMBINE_QUESTIONS_WITH_SAME_SURVEYID, SEND_QUESTIONS_WITH_SAME_SURVEYID } from "../../util/Constantes";

function StepTwoLeftContent(
  {
    customerId,
    internalData,
    // Eventos
    onUpdateGroupQuestions,
    onChangeSendModalVisibility,
  },
  ref
) {
  // states
  const [selectedItems, setSelectedItems] = useState([]);

  // hooks
  const eventBus = useEventBus();
  const [showFlashMessage] = useFlashMessage();

  // refs
  const listRef = useRef(null);

  // Reseta a listagem de perguntas avulsas.
  const resetList = function () {
    listRef && listRef.current.reset();
  };

  // Imperative Handle: Externaliza métodos e atributos úteis.
  useImperativeHandle(ref, () => ({
    selectedItems,
    reset: resetList,
  }));
  /**
   * Effects: Adiciona subscribers para lidar com mudanças nos grupos de perguntas e visibilidade das modais.
   */
  useEffect(() => {
    // Disparado ao clicar em "Combinar perguntas" em footerOptions
    eventBus.subscribe(ON_COMBINE_QUESTIONS, () => {
      const surveyIdList = mapQuestionsToSurveyIdList(selectedItems);

      const onSuccess = () => {
        // Evento: Seta as perguntas agrupadas no componente pai (StepTwo)
        onUpdateGroupQuestions([...selectedItems]);
        // Resetamos a listagem
        resetList();
        // Chama a flash message indicando sucesso na operação
        showFlashMessage(
          COMBINED_QUESTIONS.title,
          COMBINED_QUESTIONS.message,
          'success'
        );
      };

      const onError = () => {
        const { title, message } = COMBINE_QUESTIONS_WITH_SAME_SURVEYID;
        showFlashMessage(title, message, 'warning');
      };

      handleOnExecuteCombinationActions(
        surveyIdList,
        null,
        onSuccess,
        onError
      );
    });

    // Disparado ao clicar em abrir a modal de combinação
    eventBus.subscribe(ON_OPEN_SEND_COMBINATION_MODAL, () => {
      const surveyIdList = mapQuestionsToSurveyIdList(selectedItems);

      const onSuccess = () => {
        // Evento: modal de envio de perguntas combinadas será aberta.
        onChangeSendModalVisibility();
      };

      const onError = () => {
        const { title, message } = SEND_QUESTIONS_WITH_SAME_SURVEYID;
        showFlashMessage(title, message, 'warning');
      };

      handleOnExecuteCombinationActions(
        surveyIdList,
        null,
        onSuccess,
        onError
      );
    });
  }, [selectedItems, onUpdateGroupQuestions, onChangeSendModalVisibility]);

  const handleOnChangeSelection = useCallback(
    (sel) => {
      setSelectedItems([...sel]);

      eventBus.notify(ON_RENDER_EVOLUTIVE_CROSS_FOOTER_OPTIONS, {
        count: sel.length,
        visible: !!sel.length,
        type: "questions",
      });
    },
    [setSelectedItems]
  );

  const Children = useCallback(
    (childrenProps) => {
      const { isOpen } = childrenProps;
      const [sid, qid] = childrenProps.id.split("_");

      return (
        <CrossQuestionsAlternatives
          surveyId={sid}
          questionId={qid}
          customerId={customerId}
          isOpen={isOpen}
          listOnlyAlternatives
        />
      );
    },
    [customerId]
  );

  return (
    <Ui.ContentLeft>
      <span className="titleWrapper">
        <MatchIcon />
        <h2 className="leftTitle">
          Perguntas avulsas - Todas as perguntas secundárias das avaliações
          selecionadas
        </h2>
      </span>
      {!!selectedItems.length &&       
        <Ui.CounterContainer>
          {selectedItems.length}
          {selectedItems.length >= 2 
          ? ' Perguntas selecionadas' 
          : ' Pergunta selecionada'}
        </Ui.CounterContainer>
      }
      <GenericCollapsibleList
        data={internalData}
        ref={listRef}
        withExpansibleItems
        withControlledItems
        itemCheckboxStyle={"square"}
        selectable
        onChangeSelection={handleOnChangeSelection}
        customItemProps={{
          maxHeight: "none",
        }}
        ChildrenComponent={Children}
      />
    </Ui.ContentLeft>
  );
}

export default forwardRef(StepTwoLeftContent);
